import type { ElementType } from 'react';
import {
  AlertOctagon,
  AlertTriangle,
  Award,
  Briefcase,
  Calendar,
  CheckSquare,
  ClipboardCheck,
  CreditCard,
  DollarSign,
  Edit,
  FileText,
  Mail,
  Package,
  Receipt,
  RefreshCw,
  Scale,
  SearchCheck,
  ShieldAlert,
  Target,
  User,
  UserPlus,
  Wrench, Inbox,
} from 'lucide-react';

// ─── Primed cache for types not in the hardcoded switch ──────────────────────
// Components can call primeTypeIdentityCache() with metadata fetched from the
// /api/v1/work-item-types endpoint so that the default case in
// getWorkItemTypeIdentity() returns real label/color/icon data instead of
// just humanizing the type_key.

interface PrimedTypeMeta {
  type_label: string;
  color: string;
  icon?: string | null;
}

const primedCache = new Map<string, PrimedTypeMeta>();

export function primeTypeIdentityCache(entries: Array<{ type_key: string; type_label: string; color: string; icon?: string | null }>): void {
  for (const entry of entries) {
    primedCache.set(entry.type_key, {
      type_label: entry.type_label,
      color: entry.color,
      icon: entry.icon ?? null,
    });
  }
}

export function clearTypeIdentityCache(): void {
  primedCache.clear();
}

export interface WorkItemTypeIdentity {
  color: string;
  gradient: string;
  borderColor: string;
  icon: ElementType;
  label: string;
  badge: string;
}

export interface WorkItemTypeAccent {
  color: string;
  tint: string;
  tintStrong: string;
  border: string;
  ring: string;
  shadow: string;
  surfaceGradient: string;
}

export function hexToRgba(hex: string, alpha: number): string {
  const normalized = hex.trim().replace(/^#/, '');

  if (!/^[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(normalized)) {
    return `rgba(59, 130, 246, ${alpha})`;
  }

  const fullHex = normalized.length === 3
    ? normalized.split('').map((char) => `${char}${char}`).join('')
    : normalized;

  const numeric = Number.parseInt(fullHex, 16);
  const r = (numeric >> 16) & 255;
  const g = (numeric >> 8) & 255;
  const b = numeric & 255;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function getWorkItemTypeIdentity(
  itemType?: string | null,
  fallbackColor = '#3b82f6'
): WorkItemTypeIdentity {
  switch (itemType) {
    case 'customer':
      return { color: '#16a34a', gradient: 'from-emerald-600 to-green-500', borderColor: 'border-t-green-500', icon: UserPlus, label: 'Customer', badge: 'bg-emerald-500/30 text-emerald-100' };
    case 'supplier':
      return { color: '#f97316', gradient: 'from-orange-500 to-amber-500', borderColor: 'border-t-orange-500', icon: Package, label: 'Supplier', badge: 'bg-orange-500/30 text-orange-100' };
    case 'partner':
      return { color: '#0ea5e9', gradient: 'from-sky-600 to-blue-500', borderColor: 'border-t-sky-500', icon: Award, label: 'Partner', badge: 'bg-sky-500/30 text-sky-100' };
    case 'company':
      return { color: '#0891b2', gradient: 'from-cyan-600 to-teal-500', borderColor: 'border-t-cyan-500', icon: Briefcase, label: 'Company', badge: 'bg-cyan-500/30 text-cyan-100' };
    case 'contractor':
      return { color: '#f59e0b', gradient: 'from-amber-500 to-yellow-500', borderColor: 'border-t-amber-500', icon: Wrench, label: 'Contractor', badge: 'bg-amber-500/30 text-amber-100' };
    case 'other':
      return { color: '#64748b', gradient: 'from-slate-600 to-slate-500', borderColor: 'border-t-slate-500', icon: FileText, label: 'Stakeholder', badge: 'bg-slate-500/30 text-slate-100' };
    case 'appointment':
      return { color: '#16a34a', gradient: 'from-green-600 to-emerald-500', borderColor: 'border-t-green-500', icon: Calendar, label: 'Appointment', badge: 'bg-green-500/30 text-green-100' };
    case 'job':
      return { color: '#3b82f6', gradient: 'from-blue-700 to-blue-500', borderColor: 'border-t-blue-500', icon: Wrench, label: 'Job Card', badge: 'bg-blue-500/30 text-blue-100' };
    case 'recurring_job':
      return { color: '#7c3aed', gradient: 'from-purple-700 to-purple-500', borderColor: 'border-t-purple-500', icon: RefreshCw, label: 'Recurring Job', badge: 'bg-purple-500/30 text-purple-100' };
    case 'ticket':
      return { color: '#7c3aed', gradient: 'from-violet-700 to-violet-500', borderColor: 'border-t-violet-500', icon: AlertTriangle, label: 'Service Ticket', badge: 'bg-violet-500/30 text-violet-100' };
    case 'task':
      return { color: '#2563eb', gradient: 'from-blue-700 to-blue-500', borderColor: 'border-t-blue-500', icon: CheckSquare, label: 'Task', badge: 'bg-blue-500/30 text-blue-100' };
    case 'email_task':
      return { color: '#dc2626', gradient: 'from-red-700 to-red-500', borderColor: 'border-t-red-500', icon: Mail, label: 'Email Task', badge: 'bg-red-500/30 text-red-100' };
    case 'project':
      return { color: '#6366f1', gradient: 'from-indigo-700 to-indigo-500', borderColor: 'border-t-indigo-500', icon: Briefcase, label: 'Project', badge: 'bg-indigo-500/30 text-indigo-100' };
    case 'invoice':
      return { color: '#ea580c', gradient: 'from-orange-700 to-orange-500', borderColor: 'border-t-orange-500', icon: Receipt, label: 'Invoice', badge: 'bg-orange-500/30 text-orange-100' };
    case 'billing_submission':
      return { color: '#0f766e', gradient: 'from-teal-700 to-emerald-500', borderColor: 'border-t-teal-500', icon: ClipboardCheck, label: 'Billing Submission', badge: 'bg-teal-500/30 text-teal-100' };
    case 'quote':
      return { color: '#0284c7', gradient: 'from-sky-700 to-sky-500', borderColor: 'border-t-sky-500', icon: FileText, label: 'Quote', badge: 'bg-sky-500/30 text-sky-100' };
    case 'quote_request':
      return { color: '#c026d3', gradient: 'from-fuchsia-700 to-fuchsia-500', borderColor: 'border-t-fuchsia-500', icon: ClipboardCheck, label: 'Quote Request', badge: 'bg-fuchsia-500/30 text-fuchsia-100' };
    case 'proforma':
      return { color: '#db2777', gradient: 'from-pink-700 to-pink-500', borderColor: 'border-t-pink-500', icon: FileText, label: 'Proforma Invoice', badge: 'bg-pink-500/30 text-pink-100' };
    case 'sales_order':
      return { color: '#8b5cf6', gradient: 'from-violet-700 to-purple-500', borderColor: 'border-t-violet-500', icon: Briefcase, label: 'Sales Order', badge: 'bg-violet-500/30 text-violet-100' };
    case 'work_order':
      return { color: '#4f46e5', gradient: 'from-indigo-700 to-indigo-500', borderColor: 'border-t-indigo-500', icon: Wrench, label: 'Work Order', badge: 'bg-indigo-500/30 text-indigo-100' };
    case 'delivery_note':
      return { color: '#f97316', gradient: 'from-orange-600 to-rose-500', borderColor: 'border-t-orange-500', icon: Package, label: 'Delivery Note', badge: 'bg-orange-500/30 text-orange-100' };
    case 'waybill':
      return { color: '#f59e0b', gradient: 'from-amber-600 to-orange-500', borderColor: 'border-t-amber-500', icon: Package, label: 'Waybill', badge: 'bg-amber-500/30 text-amber-100' };
    case 'inventory':
      return { color: '#ca8a04', gradient: 'from-yellow-700 to-yellow-500', borderColor: 'border-t-yellow-500', icon: Package, label: 'Inventory', badge: 'bg-yellow-500/30 text-yellow-100' };
    case 'asset':
      return { color: '#64748b', gradient: 'from-slate-700 to-slate-500', borderColor: 'border-t-slate-500', icon: Target, label: 'Asset', badge: 'bg-slate-500/30 text-slate-100' };
    case 'supplier_invoice':
      return { color: '#b91c1c', gradient: 'from-red-800 to-red-600', borderColor: 'border-t-red-700', icon: Receipt, label: 'Supplier Invoice', badge: 'bg-red-600/30 text-red-100' };
    case 'credit_note':
      return { color: '#0d9488', gradient: 'from-teal-700 to-teal-500', borderColor: 'border-t-teal-500', icon: CreditCard, label: 'Credit Note', badge: 'bg-teal-500/30 text-teal-100' };
    case 'debit_note':
      return { color: '#be185d', gradient: 'from-pink-800 to-fuchsia-600', borderColor: 'border-t-pink-600', icon: Receipt, label: 'Debit Note', badge: 'bg-pink-600/30 text-pink-100' };
    case 'purchase_order':
      return { color: '#4338ca', gradient: 'from-indigo-800 to-indigo-600', borderColor: 'border-t-indigo-600', icon: Briefcase, label: 'Purchase Order', badge: 'bg-indigo-600/30 text-indigo-100' };
    case 'supplier_credit':
      return { color: '#0891b2', gradient: 'from-cyan-700 to-cyan-500', borderColor: 'border-t-cyan-500', icon: CreditCard, label: 'Supplier Credit', badge: 'bg-cyan-500/30 text-cyan-100' };
    case 'supplier_quote':
      return { color: '#2563eb', gradient: 'from-blue-700 to-blue-500', borderColor: 'border-t-blue-500', icon: FileText, label: 'Supplier Quote', badge: 'bg-blue-500/30 text-blue-100' };
    case 'grn':
      return { color: '#0f766e', gradient: 'from-teal-700 to-emerald-500', borderColor: 'border-t-teal-500', icon: Package, label: 'Goods Received Note', badge: 'bg-teal-500/30 text-teal-100' };
    case 'purchase_requisition':
      return { color: '#3b82f6', gradient: 'from-blue-700 to-sky-500', borderColor: 'border-t-blue-500', icon: ClipboardCheck, label: 'Purchase Requisition', badge: 'bg-blue-500/30 text-blue-100' };
    case 'purchase_return':
      return { color: '#312e81', gradient: 'from-indigo-900 to-indigo-600', borderColor: 'border-t-indigo-700', icon: RefreshCw, label: 'Purchase Return', badge: 'bg-indigo-600/30 text-indigo-100' };
    case 'rfq':
      return { color: '#0369a1', gradient: 'from-sky-800 to-sky-600', borderColor: 'border-t-sky-600', icon: SearchCheck, label: 'Request for Quote', badge: 'bg-sky-600/30 text-sky-100' };
    case 'rma':
      return { color: '#6d28d9', gradient: 'from-purple-700 to-violet-500', borderColor: 'border-t-purple-500', icon: RefreshCw, label: 'Return Authorization', badge: 'bg-purple-500/30 text-purple-100' };
    case 'picking_slip':
      return { color: '#059669', gradient: 'from-emerald-700 to-emerald-500', borderColor: 'border-t-emerald-500', icon: ClipboardCheck, label: 'Picking Slip', badge: 'bg-emerald-500/30 text-emerald-100' };
    case 'packing_slip':
      return { color: '#0d9488', gradient: 'from-teal-700 to-teal-500', borderColor: 'border-t-teal-500', icon: Package, label: 'Packing Slip', badge: 'bg-teal-500/30 text-teal-100' };
    case 'stock_adjustment':
      return { color: '#0891b2', gradient: 'from-cyan-700 to-sky-500', borderColor: 'border-t-cyan-500', icon: Target, label: 'Stock Adjustment', badge: 'bg-cyan-500/30 text-cyan-100' };
    case 'transfer_order':
      return { color: '#0f766e', gradient: 'from-teal-800 to-cyan-600', borderColor: 'border-t-teal-600', icon: RefreshCw, label: 'Transfer Order', badge: 'bg-teal-600/30 text-teal-100' };
    case 'lead':
      return { color: '#d97706', gradient: 'from-amber-700 to-amber-500', borderColor: 'border-t-amber-500', icon: UserPlus, label: 'Lead', badge: 'bg-amber-500/30 text-amber-100' };
    case 'visitor':
      return { color: '#0d9488', gradient: 'from-teal-700 to-teal-500', borderColor: 'border-t-teal-500', icon: UserPlus, label: 'Visitor', badge: 'bg-teal-500/30 text-teal-100' };
    case 'audit':
      return { color: '#4338ca', gradient: 'from-indigo-800 to-indigo-600', borderColor: 'border-t-indigo-600', icon: ClipboardCheck, label: 'Audit', badge: 'bg-indigo-600/30 text-indigo-100' };
    case 'certification':
      return { color: '#059669', gradient: 'from-emerald-700 to-emerald-500', borderColor: 'border-t-emerald-500', icon: Award, label: 'Certification', badge: 'bg-emerald-500/30 text-emerald-100' };
    case 'hazard':
      return { color: '#dc2626', gradient: 'from-red-700 to-orange-600', borderColor: 'border-t-red-600', icon: AlertOctagon, label: 'Hazard', badge: 'bg-red-500/30 text-red-100' };
    case 'inspection':
      return { color: '#0369a1', gradient: 'from-sky-800 to-sky-600', borderColor: 'border-t-sky-600', icon: SearchCheck, label: 'Inspection', badge: 'bg-sky-600/30 text-sky-100' };
    case 'incident':
      return { color: '#9f1239', gradient: 'from-rose-800 to-rose-600', borderColor: 'border-t-rose-600', icon: ShieldAlert, label: 'Incident', badge: 'bg-rose-600/30 text-rose-100' };
    case 'team_member':
      return { color: '#fc6500', gradient: 'from-orange-600 to-amber-500', borderColor: 'border-t-orange-500', icon: User, label: 'Team Member', badge: 'bg-orange-500/30 text-orange-100' };
    case 'matter':
      return { color: '#7c3aed', gradient: 'from-purple-700 to-indigo-600', borderColor: 'border-t-purple-500', icon: Scale, label: 'Matter', badge: 'bg-purple-500/30 text-purple-100' };
    case 'court_date':
      return { color: '#b45309', gradient: 'from-amber-700 to-orange-500', borderColor: 'border-t-amber-500', icon: Calendar, label: 'Court Date', badge: 'bg-amber-500/30 text-amber-100' };
    case 'contract':
      return { color: '#4338ca', gradient: 'from-indigo-700 to-purple-600', borderColor: 'border-t-indigo-500', icon: FileText, label: 'Contract', badge: 'bg-indigo-500/30 text-indigo-100' };
    case 'trust_transaction':
      return { color: '#047857', gradient: 'from-emerald-700 to-teal-600', borderColor: 'border-t-emerald-500', icon: DollarSign, label: 'Trust Transaction', badge: 'bg-emerald-500/30 text-emerald-100' };
    case 'din':
      return { color: '#0891b2', gradient: 'from-cyan-700 to-cyan-500', borderColor: 'border-t-cyan-500', icon: Inbox, label: 'Document Inbox', badge: 'bg-cyan-500/30 text-cyan-100' };
    default: {
      const primed = itemType ? primedCache.get(itemType) : null;
      if (primed) {
        return {
          color: primed.color,
          gradient: '',
          borderColor: 'border-t-blue-500',
          icon: Edit,
          label: primed.type_label,
          badge: 'bg-white/20 text-white',
        };
      }
      return {
        color: fallbackColor,
        gradient: '',
        borderColor: 'border-t-blue-500',
        icon: Edit,
        label: (itemType || 'Item').replace(/_/g, ' ').replace(/\b\w/g, (letter: string) => letter.toUpperCase()),
        badge: 'bg-white/20 text-white',
      };
    }
  }
}

export function getWorkItemTypeAccent(
  itemType?: string | null,
  fallbackColor = '#3b82f6'
): WorkItemTypeAccent {
  const identity = getWorkItemTypeIdentity(itemType, fallbackColor);

  return {
    color: identity.color,
    tint: hexToRgba(identity.color, 0.08),
    tintStrong: hexToRgba(identity.color, 0.14),
    border: hexToRgba(identity.color, 0.24),
    ring: hexToRgba(identity.color, 0.3),
    shadow: hexToRgba(identity.color, 0.18),
    surfaceGradient: `linear-gradient(135deg, ${hexToRgba(identity.color, 0.16)} 0%, ${hexToRgba(identity.color, 0.08)} 55%, rgba(255, 255, 255, 0) 100%)`,
  };
}